import { Check, Pencil, Plus, Tags, Trash2, X } from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import type { AgentTag } from "@/api/generated/AgentTag"
import { describeError } from "@/api/mutation"
import { useAgentTags, useCreateTag, useDeleteTag, useUpdateTag } from "@/api/queries/agents"
import { useConfirm } from "@/components/app/confirm-dialog"
import { EmptyState, ErrorState } from "@/components/app/states"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Skeleton } from "@/components/ui/skeleton"
import { fire } from "@/features/studio/fire"
import { cn } from "@/lib/utils"

export const TAG_COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#ec4899",
  "#ef4444",
  "#f97316",
  "#f59e0b",
  "#84cc16",
  "#22c55e",
  "#14b8a6",
  "#06b6d4",
  "#3b82f6",
  "#64748b",
] as const

/** Coloured tag chip (custom colour as dot + tinted border). */
export function TagBadge({
  tag,
  onRemove,
  className,
}: {
  tag: Pick<AgentTag, "name" | "color">
  onRemove?: () => void
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <span
      data-slot="tag-badge"
      className={cn(
        "bg-background inline-flex h-5 max-w-40 shrink-0 items-center gap-1 rounded-md border px-1.5 text-xs font-medium",
        onRemove && "pr-0.5",
        className,
      )}
      style={{ borderColor: `${tag.color}55` }}
    >
      <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: tag.color }} />
      <span className="truncate">{tag.name}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="text-muted-foreground hover:text-foreground hover:bg-muted rounded p-0.5"
          aria-label={t("studio.tags.remove", { name: tag.name })}
        >
          <X className="size-3" />
        </button>
      )}
    </span>
  )
}

/** Inline list of tag chips for a set of ids (unknown ids are skipped). */
export function TagList({
  ids,
  tags,
  max = 3,
}: {
  ids: number[]
  tags: AgentTag[] | undefined
  max?: number
}) {
  const items = ids.map((id) => tags?.find((x) => x.id === id)).filter((x): x is AgentTag => !!x)
  if (!items.length) return null
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1">
      {items.slice(0, max).map((tag) => (
        <TagBadge key={tag.id} tag={tag} />
      ))}
      {items.length > max && <span className="text-muted-foreground text-xs">+{items.length - max}</span>}
    </span>
  )
}

function ColorSwatches({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {TAG_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          aria-label={c}
          aria-pressed={value.toLowerCase() === c}
          className="ring-offset-background focus-visible:ring-ring flex size-5 items-center justify-center rounded-full ring-offset-2 transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:outline-none aria-pressed:ring-2 aria-pressed:ring-foreground/40"
          style={{ backgroundColor: c }}
        >
          {value.toLowerCase() === c && <Check className="size-3 text-white" />}
        </button>
      ))}
      <label className="relative flex size-5 cursor-pointer items-center justify-center overflow-hidden rounded-full border">
        <span className="sr-only">{t("studio.tags.customColor")}</span>
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 size-full cursor-pointer opacity-0"
        />
        <Plus className="text-muted-foreground size-3" />
      </label>
    </div>
  )
}

/**
 * Multi-select of agent tags in a popover (with inline "create tag").
 * Renders its own trigger (`children`) or a default "Tags" button.
 */
export function TagPicker({
  value,
  onChange,
  children,
  align = "start",
}: {
  value: number[]
  onChange: (ids: number[]) => void
  children?: React.ReactElement
  align?: "start" | "end"
}) {
  const { t } = useTranslation()
  const tags = useAgentTags()
  const create = useCreateTag()
  const [query, setQuery] = React.useState("")
  const filtered = (tags.data ?? []).filter((x) => x.name.toLowerCase().includes(query.trim().toLowerCase()))
  const exact = (tags.data ?? []).some((x) => x.name.toLowerCase() === query.trim().toLowerCase())
  const toggle = (id: number) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id])
  const createTag = async () => {
    const name = query.trim()
    if (!name) return
    const tag = await create.mutateAsync({
      name,
      color: TAG_COLORS[(tags.data?.length ?? 0) % TAG_COLORS.length]!,
    })
    onChange([...value, tag.id])
    setQuery("")
  }
  return (
    <Popover>
      <PopoverTrigger
        render={
          children ?? (
            <Button type="button" variant="outline" size="sm">
              <Tags />
              {t("studio.tags.title")}
            </Button>
          )
        }
      />
      <PopoverContent align={align} className="w-64 gap-0 p-0">
        <div className="border-b p-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("studio.tags.searchOrCreate")}
            className="h-7 text-xs"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                if (!exact && query.trim()) fire(createTag())
              }
            }}
            aria-label={t("studio.tags.searchOrCreate")}
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {tags.isPending ? (
            <div className="space-y-1 p-1">
              <Skeleton className="h-6" />
              <Skeleton className="h-6" />
            </div>
          ) : (
            filtered.map((tag) => {
              const on = value.includes(tag.id)
              return (
                <button
                  key={tag.id}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={on}
                  onClick={() => toggle(tag.id)}
                  className="hover:bg-muted focus-visible:bg-muted flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm outline-none"
                >
                  <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: tag.color }} />
                  <span className="min-w-0 flex-1 truncate">{tag.name}</span>
                  {on && <Check className="text-primary size-3.5" />}
                </button>
              )
            })
          )}
          {!tags.isPending && filtered.length === 0 && !query.trim() && (
            <p className="text-muted-foreground px-2 py-3 text-center text-xs">{t("studio.tags.none")}</p>
          )}
          {query.trim() && !exact && (
            <button
              type="button"
              onClick={() => fire(createTag())}
              disabled={create.isPending}
              className="hover:bg-muted text-primary flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm"
            >
              <Plus className="size-3.5" />
              <span className="truncate">{t("studio.tags.create", { name: query.trim() })}</span>
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function TagRow({ tag }: { tag: AgentTag }) {
  const { t } = useTranslation()
  const update = useUpdateTag()
  const remove = useDeleteTag()
  const confirm = useConfirm()
  const [editing, setEditing] = React.useState(false)
  const [name, setName] = React.useState(tag.name)
  const [color, setColor] = React.useState(tag.color)
  const [error, setError] = React.useState<string>()

  const save = async () => {
    setError(undefined)
    try {
      await update.mutateAsync({ id: tag.id, name: name.trim(), color })
      setEditing(false)
    } catch (e) {
      setError(describeError(e, t))
    }
  }

  if (editing)
    return (
      <li className="bg-muted/30 space-y-2.5 rounded-lg border p-3">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          aria-label={t("studio.tags.name")}
          aria-invalid={!!error || undefined}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save()
            if (e.key === "Escape") setEditing(false)
          }}
        />
        <ColorSwatches value={color} onChange={setColor} />
        {error && <p className="text-destructive text-xs">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
            {t("common.cancel")}
          </Button>
          <Button size="sm" onClick={() => void save()} disabled={!name.trim() || update.isPending}>
            {t("common.save")}
          </Button>
        </div>
      </li>
    )

  return (
    <li className="hover:bg-muted/40 group flex items-center gap-3 rounded-lg px-2 py-1.5">
      <TagBadge tag={tag} />
      <span className="text-muted-foreground text-xs tabular-nums">
        {t("studio.tags.agentCount", { count: tag.agent_count })}
      </span>
      <div className="ml-auto flex gap-0.5 opacity-70 group-hover:opacity-100">
        <Button
          size="icon-sm"
          variant="ghost"
          onClick={() => {
            setName(tag.name)
            setColor(tag.color)
            setEditing(true)
          }}
          aria-label={t("common.edit")}
        >
          <Pencil />
        </Button>
        <Button
          size="icon-sm"
          variant="ghost"
          className="hover:text-destructive"
          aria-label={t("studio.tags.delete")}
          onClick={() =>
            void confirm({
              title: t("studio.tags.deleteTitle", { name: tag.name }),
              description: t("studio.tags.deleteDescription", { count: tag.agent_count }),
              confirmLabel: t("studio.tags.delete"),
              destructive: true,
              onConfirm: () => remove.mutateAsync(tag),
            })
          }
        >
          <Trash2 />
        </Button>
      </div>
    </li>
  )
}

/** Dialog to create, rename, recolour and delete agent tags. */
export function TagManagerDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation()
  const tags = useAgentTags()
  const create = useCreateTag()
  const [name, setName] = React.useState("")
  const [color, setColor] = React.useState<string>(TAG_COLORS[0])
  const [error, setError] = React.useState<string>()

  const add = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setError(undefined)
    try {
      await create.mutateAsync({ name: name.trim(), color })
      setName("")
      setColor(TAG_COLORS[((tags.data?.length ?? 0) + 1) % TAG_COLORS.length]!)
    } catch (err) {
      setError(describeError(err, t))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("studio.tags.manage")}</DialogTitle>
          <DialogDescription>{t("studio.tags.manageDescription")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={add} className="space-y-2.5 rounded-lg border p-3">
          <div className="flex gap-2">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("studio.tags.newPlaceholder")}
              aria-label={t("studio.tags.name")}
              aria-invalid={!!error || undefined}
              maxLength={64}
            />
            <Button type="submit" disabled={!name.trim() || create.isPending}>
              <Plus />
              {t("studio.tags.add")}
            </Button>
          </div>
          <ColorSwatches value={color} onChange={setColor} />
          {error && <p className="text-destructive text-xs">{error}</p>}
        </form>
        <div className="-mx-1 max-h-80 overflow-y-auto">
          {tags.isError ? (
            <ErrorState error={tags.error} onRetry={() => void tags.refetch()} variant="plain" />
          ) : tags.isPending ? (
            <div className="space-y-2 px-1">
              <Skeleton className="h-7" />
              <Skeleton className="h-7" />
            </div>
          ) : tags.data.length === 0 ? (
            <EmptyState variant="plain" size="sm" icon={Tags} title={t("studio.tags.empty")} />
          ) : (
            <ul className="space-y-0.5 px-1">
              {tags.data.map((tag) => (
                <TagRow key={tag.id} tag={tag} />
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
